<!-- eslint-disable max-len -->
<!-- eslint-disable max-len -->
<template>
  <div class="upload-and-chat">
    <!-- Main Upload Area -->
    <div class="upload-main">
      <div class="upload-header">
        <Title>
          {{ documentTitle }}
        </Title>
      </div>

      <div v-if="clientPortalStore.selectedDocument?.document">
        <Callout
          status="success"
          spacing="loose"
        >
          Step Complete<br>
          The file <strong>
            {{ clientPortalStore.selectedDocument?.document?.filename }}</strong>
          <span v-if="clientPortalStore.selectedDocument?.document?.uploaded_at">
            was uploaded on
            <strong>{{ formatDate(clientPortalStore.selectedDocument?.document?.uploaded_at) }}
            </strong>
          </span>
          <span v-else>has been uploaded.</span>
        </Callout>
      </div>

      <!-- File Upload Zone -->
      <DocumentPortalControls
        v-else-if="clientPortalStore.selectedDocument"
        :key="clientPortalStore.selectedDocument?.sequentialNumber"
        :document="clientPortalStore.selectedDocument"
        :current-entity-id="clientPortalStore.currentEntityId"
        :advisor-slug="clientPortalStore.advisorSlug"
        :csrf-token="clientPortalStore.csrfToken"
        :is-loading="clientPortalStore.isLoading"
        :disabled="clientPortalStore.advisorView"
        @move-to-next-document="moveToNextDocument"
        @documents-updated="refreshDocuments"
        @document-classified="handleDocumentClassified"
        @checklist-item-crossed-off="handleChecklistCrossOff"
        @upload-complete="handleUploadComplete"
        @chat-message="handleChatMessage"
        @mark-not-applicable="markNotApplicable"
        @mark-dont-have="markDontHave"
        @mark-declined="markDeclined"
        @delete-document="handleDeleteDocument"
      />

      <Buttons>
        <Button
          v-if="clientPortalStore.currentStepNumber !== clientPortalStore.totalDocuments"
          :disabled="
            clientPortalStore.selectedDocument?.status === 'pending' || somethingIsLoading ||
            !clientPortalStore.processComplete && clientPortalStore.currentStepNumber === clientPortalStore.totalDocuments
          "
          variant="success"
          @click="moveToNextDocument"
        >
        Proceed to next document
        </Button>
        <Button
          v-if="clientPortalStore.selectedDocument?.document"
          :disabled="somethingIsLoading"
          @click="handleDeleteDocument(clientPortalStore.selectedDocument?.document?.id, clientPortalStore.selectedDocument?.document?.filename)"
          variant="danger"
        >
          Delete Document
        </Button>
      </Buttons>
    </div>

    <!-- Chat Assistant -->
    <div
      v-if="selectedDocumentForChat?.sectionKey !== 'other'"
      class="chat-section"
    >
      <div
        ref="chatMessagesEl"
        class="chat-messages"
      >
        <!-- Welcome message when no document selected -->
        <div
          v-if="!selectedDocumentForChat"
          class="chat-welcome"
        >
          <div class="welcome-icon">
            <Icon icon="fas fa-magic" />
          </div>
          <p class="welcome-text">
            Select a document from the checklist to get help finding and uploading it.
          </p>
        </div>

        <!-- Chat Messages -->
        <div
          v-for="message in chatMessages"
          :key="message.id"
          class="chat-message"
          :class="{ 'user-message': message.role === 'user' }"
        >
          <div class="message-content">
            <div
              v-if="message.role === 'assistant'"
              v-html="formatMessage(message.content)"
            />
            <div v-else>
              {{ message.content }}
            </div>
          </div>
        </div>

        <!-- Loading indicator -->
        <div
          v-if="isLoading"
          class="chat-message loading-message"
        >
          <div class="message-content loading-content">
            <div class="loading-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      </div>

      <!-- AI Assistant Input - Fixed at bottom of chat -->
      <div class="chat-input-section">
        <div class="assistant-input-container stk-input">
          <Icon
            icon="fas fa-magic"
            class="assistant-icon"
          />
          <input
            v-model="assistantQuestion"
            type="text"
            placeholder="Ask the AI Document Assistant"
            class="assistant-input"
            :disabled="isLoading || !selectedDocumentForChat"
            @keyup.enter="askAssistant"
          >
          <IconButton
            icon="fas fa-sync-alt"
            title="Clear chat"
            @click="clearChat"
          />
          <button
            class="assistant-submit"
            :disabled="!assistantQuestion.trim() || isLoading || !selectedDocumentForChat"
            @click="askAssistant"
          >
            Ask
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, watch } from 'vue';
import { marked } from 'marked';
import { Buttons, Button, Callout, Icon, IconButton, Subtitle, Title } from '@stakeui';
import DocumentPortalControls from '../../client-portal/components/DocumentPortalControls.vue';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';
import { useDocumentUploadStore } from '@frontend/ui/stores/DocmasterStore';
import { formatDate, prettifyMixedCase } from '@frontend/shared/Util.js';

export default {
  name: 'DocumentPortalUpload',
  components: {
    Buttons,
    Button,
    Callout,
    Icon,
    IconButton,
    DocumentPortalControls,
    Subtitle,
    Title,
  },
  props: {
    firstDocument: {
      type: Object,
      required: false,
      default: null,
    },
  },
  emits: ['back-to-main'],
  setup(props, { emit }) {
    const clientPortalStore = useClientPortalStore();
    const documentUploadStore = useDocumentUploadStore();
    const assistantQuestion = ref('');
    const chatMessages = ref([]);
    const isLoading = ref(false);

    // File input refs
    const chatMessagesEl = ref(null);

    // Computed properties for the chat functionality
    const selectedDocumentForChat = computed(() => {
      const selected = clientPortalStore.selectedDocument;
      return selected ?
      {
        name: selected.name,
        sectionKey: selected.sectionKey,
        sectionName: selected.sectionName,
        originalItem: selected.originalItem,
      } :
      null;
    });

    const documentTitle = computed(() => prettifyMixedCase(clientPortalStore.currentDocumentName));

    const somethingIsLoading = computed(() => clientPortalStore.isLoading || documentUploadStore.isLoading);

    // Methods
    const markNotApplicable = async () => {
      if (clientPortalStore.advisorView) {
        return;
      }
      await clientPortalStore.markDocument(clientPortalStore.selectedDocument.id, clientPortalStore.selectedDocument.status, 'not_applicable');
      await refreshDocuments();
      addChatMessage('assistant', `Marked "${clientPortalStore.currentDocumentName}" as not applicable. Moving to the next document.`);
    };

    const markDontHave = async () => {
      if (clientPortalStore.advisorView) {
        return;
      }
      await clientPortalStore.markDocument(clientPortalStore.selectedDocument.id, clientPortalStore.selectedDocument.status, 'dont_have');
      await refreshDocuments();
      addChatMessage('assistant', `Noted that you don't have "${clientPortalStore.currentDocumentName}". Moving to the next document.`);
    };

    const markDeclined = async () => {
      if (clientPortalStore.advisorView) {
        return;
      }
      await clientPortalStore.markDocument(clientPortalStore.selectedDocument.id, clientPortalStore.selectedDocument.status, 'not_comfortable');
      await refreshDocuments();
      addChatMessage('assistant', `I understand you're not comfortable uploading "${clientPortalStore.currentDocumentName}". Moving to the next document.`);
    };

    // Event handlers for DocMasterUploadClient
    const refreshDocuments = async () => {
      try {
        // Reload checklist data
        await clientPortalStore.loadChecklistData();
      } catch (error) {
        console.error('Error refreshing documents:', error);
      }
    };

    const handleUploadComplete = data => {
      console.log('Upload completed:', data);
      clientPortalStore.markUploaded();
      addChatMessage('assistant', 'Your documents have been uploaded successfully!');
      // Start polling to see if one of the document's status changes
      clientPortalStore.startPollingChecklist();
    };

    const handleChecklistCrossOff = async data => {
      console.log('Checklist item crossed off:', data);

      // If we have the necessary data, use our cross-off function
      if (data.documentId && data.entityId && data.classification) {
        try {
          await triggerChecklistCrossOff(data.documentId, data.entityId, data.classification);
          clientPortalStore.markUploaded();
        } catch (error) {
          console.error('Failed to cross off checklist item:', error);
        }
      } else {
        // Fallback: just refresh the documents
        refreshDocuments();
      }
    };

    const handleDocumentClassified = async data => {
      console.log('Document classified:', data);
      const doc = data.document;
      if (doc && doc.classification) {
        addChatMessage('assistant', `I've identified ${doc.name} as: ${doc.classification}`);

        // Automatically trigger checklist cross-off when document is classified
        if (doc.external_id && clientPortalStore.currentEntityId) {
          try {
            await triggerChecklistCrossOff(doc.external_id, clientPortalStore.currentEntityId, doc.classification);
            addChatMessage('assistant', `I've also updated your checklist to mark this document as completed.`);
          } catch (error) {
            console.error('Failed to cross off checklist item:', error);
            addChatMessage('assistant', `Document identified successfully, but I couldn't update the checklist automatically.`);
          }
        }
      }
      // Refresh documents
      refreshDocuments();
    };

    const handleDeleteDocument = async (documentId, filename) => {
      if (clientPortalStore.advisorView) {
        return;
      }

      if (!confirm(`Are you sure you want to delete ${filename}?`)) {
        return
      }

      try {
        await documentUploadStore.deleteDocumentUpdateChecklist(clientPortalStore.currentEntityId, documentId, clientPortalStore.csrfToken);
        addChatMessage(`Document ${filename} has been deleted.`)
        // Start polling to see if one of the document's status changes
        clientPortalStore.startPollingChecklist();
      } catch (error) {
        console.error('Failed to delete document:', error)
        addChatMessage(`Failed to delete ${filename}: ${error}`)
      }
    }

    const handleChatMessage = message => {
      addChatMessage('assistant', message);
    };

    // Cross-off checklist function (ported from legacy component)
    const triggerChecklistCrossOff = async (documentId, entityId, classification) => {
      console.log(
          `Explicitly calling cross-off-checklist API for document ${documentId} (${classification})`,
      );

      try {
        // Add detailed logging
        console.log(
            `Request payload: `,
            JSON.stringify({
              document_id: documentId,
              entity_id: entityId,
            }),
        );

        const response = await fetch('/api/v1/client-portal/cross-off-checklist/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': clientPortalStore.csrfToken,
          },
          body: JSON.stringify({
            document_id: documentId,
            entity_id: entityId,
          }),
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Cross-off checklist API response:', result);

        // Update local checklist data to reflect the crossed-off item
        if (result.checklist_item_crossed_off && checklistData.value) {
          updateLocalChecklistData(result, classification);
        }

        // Refresh checklist data from server to ensure consistency
        await clientPortalStore.loadChecklistData();

        // Dispatch event for any other components that might be listening
        const refreshEvent = new CustomEvent('refresh-document-checklist', {
          detail: {
            documentId,
            entityId,
            classification,
            checklist_item_crossed_off: result.checklist_item_crossed_off || false,
            matched_section: result.matched_section || result.best_match?.section_key || null,
          },
        });

        console.log('Dispatching refresh-document-checklist event with details:', refreshEvent.detail);
        window.dispatchEvent(refreshEvent);

        return result;
      } catch (error) {
        console.error('Error calling cross-off-checklist API:', error);
        throw error;
      }
    };

    // Update local checklist data when an item is crossed off
    const updateLocalChecklistData = (crossOffResult, classification) => {
      if (!checklistData.value?.sections) return;

      // Find the matching section and item based on the API response
      const matchedSectionKey = crossOffResult.matched_section || crossOffResult.best_match?.section_key;

      if (matchedSectionKey && checklistData.value.sections[matchedSectionKey]) {
        const section = checklistData.value.sections[matchedSectionKey];

        // Find and update the matching item
        if (section.items) {
          section.items.forEach(item => {
            // Try to match by document type or custom name
            const itemMatches =
              item.document_type === classification ||
              item.custom_document_name === classification ||
              (item.document_type && classification.toLowerCase().includes(item.document_type.toLowerCase()));

            if (itemMatches && item.status !== 'completed') {
              console.log(`Marking checklist item as completed: ${item.document_type || item.custom_document_name}`);
              item.status = 'completed';
              // Force reactivity update
              checklistData.value = { ...checklistData.value };
            }
          });
        }
      }
    };

    const moveToNextDocument = () => {
      const result = clientPortalStore.moveToNextDocument();

      if (result.completed) {
        setTimeout(() => {
          addChatMessage('assistant', 'Congratulations! You\'ve completed all required documents.');
        }, 500);
      } else {
        setTimeout(() => {
          addChatMessage('assistant', `Now let's work on: ${result.document?.name}`);
        }, 500);
      }
    };

    const showWhereToFind = () => {
      if (selectedDocumentForChat.value) {
        // Add user message and get response from AI
        askAssistantWithMessage('Where do I find this document?');
      }
    };

    const showWhatItLooksLike = () => {
      if (selectedDocumentForChat.value) {
        // Add user message and get response from AI
        askAssistantWithMessage('What does this document look like?');
      }
    };

    // Format message content with markdown (from DocumentChatAssistant)
    const formatMessage = content => {
      if (!content) {
        return;
      }

      try {
        return marked.parse(content);
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return content;
      }
    };

    const askAssistant = () => {
      if (!assistantQuestion.value.trim() || isLoading.value || !selectedDocumentForChat.value) {
        return;
      }

      const userMessage = assistantQuestion.value.trim();
      assistantQuestion.value = '';

      askAssistantWithMessage(userMessage);
    };

    // Send message to API (logic from DocumentChatAssistant)
    const askAssistantWithMessage = async userMessage => {
      if (!selectedDocumentForChat.value) return;

      // Add user message to chat
      addChatMessage('user', userMessage);

      // Show loading indicator
      isLoading.value = true;

      // Scroll to bottom to see user message
      await scrollToBottom();

      try {
        // Call API to get AI response
        const response = await fetch('/api/v1/client-portal/document-chat/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': clientPortalStore.csrfToken,
          },
          body: JSON.stringify({
            message: userMessage,
            document_type: selectedDocumentForChat.value.name,
            entity_id: clientPortalStore.currentEntityId,
            chat_history: chatMessages.value.map(msg => ({
              role: msg.role,
              content: msg.content,
            })),
          }),
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();

        // Add AI response to chat
        addChatMessage('assistant', data.response);
      } catch (error) {
        console.error('Error sending message:', error);

        // Add error message to chat
        addChatMessage('assistant', 'I\'m sorry, I encountered an error while processing your request. Please try again.');
      } finally {
        isLoading.value = false;
        await scrollToBottom();
      }
    };

    // Send initial prompt when document is selected (from DocumentChatAssistant)
    const sendInitialPrompt = async selectedDoc => {
      try {
        // Pre-bake a comprehensive prompt that requests both importance and instructions
        const initialPrompt = `Why is ${selectedDoc.name} is important for financial planning and how can I obtain it?`;

        // Check if we already have a user message (might be added by clearChat)
        if (
          chatMessages.value.length === 0 ||
          chatMessages.value[chatMessages.value.length - 1].role !== 'user'
        ) {
          // Add initial user message to chat history
          addChatMessage('user', initialPrompt);
        }

        console.log('Sending initial prompt for document type:', selectedDoc.name);

        // Try with the initialize endpoint first
        try {
          const response = await fetch('/api/v1/client-portal/document-chat/initialize/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': clientPortalStore.csrfToken,
            },
            body: JSON.stringify({
              document_type: selectedDoc.name,
              entity_id: clientPortalStore.currentEntityId,
              message: initialPrompt,
            }),
          });

          if (!response.ok) {
            console.error('Initialize endpoint failed:', response.status, response.statusText);
            throw new Error('Initialize endpoint failed');
          }

          const data = await response.json();
          console.log('Initialize response:', data);

          // Add AI response to chat
          addChatMessage('assistant', data.response);

          return; // Exit if successful
        } catch (initError) {
          console.error('Error with initialize endpoint, falling back to regular chat:', initError);
          // Fall through to regular chat endpoint
        }

        // Fallback to regular chat endpoint
        const response = await fetch('/api/v1/client-portal/document-chat/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': clientPortalStore.csrfToken,
          },
          body: JSON.stringify({
            document_type: selectedDoc.name,
            entity_id: clientPortalStore.currentEntityId,
            message: initialPrompt,
            chat_history: [], // Empty chat history since this is the first message
          }),
        });

        if (!response.ok) {
          console.error('API response not OK:', response.status, response.statusText);
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        console.log('Initial chat response:', data);

        // Add AI response to chat
        addChatMessage('assistant', data.response);
      } catch (error) {
        console.error('Error initializing chat:', error);
        // Add a fallback response message instead of throwing the error
        addChatMessage('assistant', `I'm here to help you find and obtain the "${selectedDoc.name}" document. Please let me know if you have any specific questions about how to locate or access it.`);
      }
    };

    const addChatMessage = (role, content) => {
      chatMessages.value.push({
        id: chatMessages.value.length + 1,
        role,
        content,
      });

      // Scroll to bottom
      nextTick(() => {
        scrollToBottom();
      });
    };

    // Clear chat messages (from DocumentChatAssistant)
    const clearChat = async () => {
      chatMessages.value = [];

      if (selectedDocumentForChat.value) {
        isLoading.value = true;
        try {
          // REMOVE THIS LINE - sendInitialPrompt will handle adding the user message
          // const initialPrompt = `Please explain why the ${selectedDocumentForChat.value.name} is important for financial planning and provide specific step-by-step instructions on how I can obtain this document.`;
          // addChatMessage('user', initialPrompt);

          // Just call sendInitialPrompt - it will add the user message and get the response
          await sendInitialPrompt(selectedDocumentForChat.value);
        } catch (error) {
          console.error('Error resetting chat:', error);
          addChatMessage('assistant', `I see you're looking for help with the "${selectedDocumentForChat.value.name}" document. How can I assist you in finding or uploading this document?`);
        } finally {
          isLoading.value = false;
          await scrollToBottom();
        }
      }
    };

    // Scroll chat to bottom
    const scrollToBottom = async () => {
      await nextTick();
      if (chatMessagesEl.value) {
        chatMessagesEl.value.scrollTop = chatMessagesEl.value.scrollHeight;
      }
    };

    // Watch for document selection changes and initialize chat
    watch(
        () => selectedDocumentForChat.value,
        async (newDoc, oldDoc) => {
          if (newDoc) {
            if (newDoc.sectionKey === 'other') {
              return;
            }

            // Only reset messages if selecting a different document or first selection
            if (!oldDoc || newDoc.name !== oldDoc.name) {
              console.log('Selected new document:', newDoc.name);

              // Clear previous messages when selecting a new document
              chatMessages.value = [];

              // Show loading indicator
              isLoading.value = true;

              try {
              // Get specific guidance for this document type
                await sendInitialPrompt(newDoc);
              } catch (error) {
                console.error('Error in watch handler:', error);
                // Fallback message if we can't get specific guidance
                addChatMessage('assistant', `I see you're looking for help with the "${newDoc.name}" document. How can I assist you in finding or uploading this document?`);
              } finally {
                isLoading.value = false;
                await scrollToBottom();
              }
            }
          }
        },
        { immediate: true },
    );

    return {
      clientPortalStore,
      assistantQuestion,
      chatMessages,
      isLoading,
      selectedDocumentForChat,
      documentTitle,
      somethingIsLoading,
      chatMessagesEl,
      moveToNextDocument,
      markNotApplicable,
      markDontHave,
      markDeclined,
      refreshDocuments,
      handleUploadComplete,
      handleChecklistCrossOff,
      handleDocumentClassified,
      handleChatMessage,
      handleDeleteDocument,
      triggerChecklistCrossOff,
      updateLocalChecklistData,
      showWhereToFind,
      showWhatItLooksLike,
      askAssistant,
      clearChat,
      formatMessage,
      formatDate,
    };
  },
};
</script>

<style scoped>
/* Upload and Chat Container */
.upload-and-chat {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-grow: 1;
}

/* Upload Main Content */
.upload-main {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
}

/* Chat Input Section - Fixed at bottom of chat */
.chat-input-section {
  flex-shrink: 0;
  padding: 1rem;
}

.assistant-input-container {
  background-color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--stk-text-light);
  padding: 0.5rem;
  padding-left: 0.75rem;
  background: var(--stk-background);
  border: 1px solid var(--stk-text-90);
  border-radius: 0.75rem;
  transition-duration: var(--stk-transition-duration);
  transition-property: border-color, box-shadow, color;
}

.assistant-input-container:focus-within {
  border-color: var(--stk-primary);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.assistant-icon {
  color: var(--stk-primary);
  flex-shrink: 0;
}

.assistant-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  background: transparent;
  color: var(--stk-text-normal);
}

.assistant-input::placeholder {
  color: var(--stk-text-light);
}

.assistant-input:disabled {
  color: var(--stk-text-light);
}

.assistant-submit {
  background: var(--stk-primary);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.assistant-submit:hover:not(:disabled) {
  background: #7c3aed;
}

.assistant-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Chat Section */
.chat-section {
  flex-grow: 1;
  height: 24em;
  background: white;
  border: 1px solid var(--stk-text-90);
  border-radius: 0.5em;
  margin-top: 2em;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.chat-header {
  padding: 0.5rem 1rem;
  backdrop-filter: blur(4px);
  background-color: rgba(252, 252, 253, 0.8);
  display: none;
  align-items: center;
  justify-content: space-between;
}

.chat-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--stk-text-normal);
}

.chat-clear-button {
  background: none;
  border: none;
  color: var(--stk-text-normal);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.chat-clear-button:hover {
  background: #e2e8f0;
  color: var(--stk-text-strong);
}

.chat-messages {
  flex: 1;
  padding: 1rem;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
}

.chat-welcome {
  text-align: center;
  padding: 2rem 1rem;
}

.welcome-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: #eff6ff;
  border-radius: 50%;
  color: #3b82f6;
  margin-bottom: 1rem;
}

.welcome-text {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
  margin: 0;
}

.chat-message {
  display: flex;
  align-items: flex-start;
}

.chat-message.user-message {
  justify-content: flex-end;
}

.chat-message.loading-message {
  justify-content: flex-start;
}

.message-content {
  max-width: 80%;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  line-height: 1.4;
}

.chat-message:not(.user-message):not(.loading-message) .message-content {
  background: var(--stk-background-alt);
  color: var(--stk-text-strong);
  border-bottom-left-radius: 0.25rem;
}

.chat-message.user-message .message-content {
  background: var(--stk-primary);
  color: white;
  border-bottom-right-radius: 0.25rem;
}

.loading-content {
  background: var(--stk-background-alt);
  border-bottom-left-radius: 0.25rem;
}

.loading-dots {
  display: flex;
  gap: 0.25rem;
}

.loading-dots span {
  width: 0.5rem;
  height: 0.5rem;
  background: var(--stk-primary);
  border-radius: 50%;
  animation: loading-bounce 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Style for markdown content from the assistant */
.message-content :deep(p) {
  margin-bottom: 0.5rem;
}

.message-content :deep(p:last-child) {
  margin-bottom: 0;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.message-content :deep(li) {
  margin-bottom: 0.25rem;
}

.message-content :deep(strong) {
  font-weight: 600;
}

.message-content :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}
</style>
